import * as React from 'react'
import produce from 'immer'
import { Form, Input, Spin, Button, Tooltip } from 'antd'
import { set, max, isEmpty, orderBy } from 'lodash'
import { showError, showValidationError, ValidationError } from 'helpers/errors'
import { createLabelFactory, createFieldFactory, isReadOnly } from 'helpers/formViews'
import { stopEvent } from 'helpers/events'
import { prioritizeChecklistItems } from 'helpers/jobs'
import { t } from 'helpers/i18n'
import { formatUserTime } from 'helpers/dateTime'
import InputNumber from 'elements/InputNumber'
import Checkbox from 'elements/Checkbox'

class FormView extends React.Component {
  state = {}

  componentDidMount() {
    this.fetchItem()
  }

  fetchItem = async () => {
    try {
      const { siblingRecords = [] } = this.props
      const maxPriority = max(siblingRecords.map((each) => each.priority)) || 0
      const response = await this.props.getSettings({ type: this.props.settingsType })

      this.setState({
        fieldSettings: response.value.data.fieldSettings,
        siblings: !this.props.linkTargetRecord
          ? [...siblingRecords, { priority: maxPriority + 1 }]
          : [...siblingRecords],
      })
    } catch (error) {
      showError({ error })
    }
  }

  handleSubmit = (e) => {
    stopEvent(e)

    this.props.form.validateFields((errors, values) => {
      this.setState({ validationErrors: errors }, async () => {
        if (!isEmpty(errors)) {
          showValidationError()
          return
        }

        try {
          await this.props.onSave?.(true)
          await this.props.onSave?.(false)
          await this.props.onSaveAndClose?.(this.state.siblings)
        } catch (error) {
          showError({ error })
          await this.props.onSave?.(false)
        }
      })
    })
  }

  setItemValue = (name, value) =>
    this.setState(
      produce((draft) => {
        const index = draft.siblings.findIndex((one) => one.id === this.props.linkTargetRecord?.id)

        if (name === 'complete') {
          draft.siblings[index].lastUser = value ? this.props.user.userName : null
          draft.siblings[index].lastUserDate = value ? new Date().toJSON() : null
        }

        set(draft.siblings[index], name, value)
      }),
      () => {
        if (name === 'priority') {
          this.setState(
            produce((draft) => {
              // Step 1. Take out the value that was just changed
              const otherSiblings = draft.siblings.filter(
                (each) => each.id !== this.props.linkTargetRecord?.id
              )

              // Step 2. Prioritize and shift forward
              const shiftedSiblings = prioritizeChecklistItems(otherSiblings).map((each) => {
                if (each.priority >= value) {
                  return { ...each, priority: each.priority + 1 }
                }
                return each
              })

              // Step 4. Append and sort again
              const appendedSiblings = orderBy(
                [
                  ...shiftedSiblings,
                  draft.siblings.find((one) => one.id === this.props.linkTargetRecord?.id),
                ],
                ['priority']
              )

              // Step 5. Prioritize and replace
              draft.siblings = prioritizeChecklistItems(appendedSiblings)
            }),
            this.props.onChange
          )
        }
      }
    )

  render() {
    const { parentRecord } = this.props
    const { fieldSettings, siblings } = this.state

    if (!fieldSettings || isEmpty(siblings)) {
      return <Spin />
    }

    const item = siblings.find((one) => one.id === this.props.linkTargetRecord?.id)

    const createLabel = createLabelFactory(fieldSettings)
    const createFieldDecorator = createFieldFactory(this.props.form, item, fieldSettings)

    const readOnly = isReadOnly(this)
    const { isTemplate } = parentRecord ?? {}

    return (
      <Form layout="vertical" colon={false} onSubmit={readOnly ? stopEvent : this.handleSubmit}>
        <div className="form-items-container">
          <ValidationError errors={this.state.validationErrors} />
          <Form.Item label={createLabel('description')}>
            {createFieldDecorator('description')(
              <Input
                onChange={(e) => this.setItemValue('description', e.target.value)}
                disabled={readOnly}
                autoFocus
              />
            )}
          </Form.Item>
          <Form.Item label={createLabel('notes')}>
            {createFieldDecorator('notes')(
              <Input.TextArea
                onChange={(e) => this.setItemValue('notes', e.target.value)}
                disabled={readOnly}
                rows={5}
              />
            )}
          </Form.Item>
          <Form.Item label={createLabel('priority')}>
            {createFieldDecorator('priority')(
              <InputNumber
                onChange={(value) =>
                  this.setItemValue('priority', Math.max(value, item.priority === 0 ? 0 : 1))
                }
                disabled={readOnly}
                min={item.priority === 0 ? 0 : 1}
              />
            )}
          </Form.Item>
          <Form.Item label={t('completedDate')}>
            <Input value={formatUserTime(item.lastUserDate, item.lastUser)} disabled />
          </Form.Item>
          <Form.Item>
            {createFieldDecorator('complete', { valuePropName: 'checked' })(
              <Checkbox
                onChange={(e) => this.setItemValue('complete', e.target.checked)}
                disabled={readOnly || isTemplate}
              >
                {createLabel('complete')}
              </Checkbox>
            )}
          </Form.Item>
        </div>
        <div className="form-buttons-container">
          <Button onClick={this.props.onCancel}>{t(readOnly ? 'close' : 'cancel')}</Button>{' '}
          {!readOnly && (
            <Tooltip title={t('childRecordSaveInfo')} placement="topLeft">
              <Button type="primary" htmlType="submit" loading={this.state.saveAndCloseButtonLoading}>
                {!this.props.linkTargetRecord ? t('add') : t('update')}
              </Button>
            </Tooltip>
          )}
        </div>
      </Form>
    )
  }
}

export default Form.create()(FormView)
